import { ApiStoreXmedia } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiCmsTranslations } from 'idt-ecommerce-api/_api/providers/api-cms/api-cms-translations/api-cms-translations.models';


export const DEFAULT_LANGUAGE_ID = '-1'; // EN (English)

export interface StoreSummary {
  catalogs: StoreCatalog[]; /** Listado de catálogos */
  continent: string; /** Identificador del continente */
  countryCode: string; /** código del país (mayúsculas) Ej) ES */
  countryName: string; /** Nombre del país.
                        * La teoría dice que viene localizado en el idioma solicitado pero en la práctica no suele ser así
                        */
  hostname: string; /** Identificador del host a usar en esa tienda. Ej) En china cambia el dominio. */
  id: string; /** Identificador */
  supportedLanguages: StoreLanguage[]; /** Listado de idiomas soportados */
  isOpenForSale: boolean; /** Indica si la tienda puede vender o no */
  warehouseId: number; /** Identificador del warehouse usado */
  redirectToWorldWide?: number; /** Store ID de la tienda a redirigir en paises de no venta (franquiciados). Por ej: 68009600  */
  countryISOredirect?: string; /** ISO del país usado para generar redirección a WW/WZ/WY... etc. Por ej: WW  */
}

// TODO Translations Review
// Review Update translations type with model from back-api
// Should translations be at this level? better inside summary? better elsewhere?
export interface StoreDetail extends StoreSummary {
  details: StoreDetailDetails; /** Detalles de la tienda */
  storeDefaultLanguageId?: string; /** Identificador del idioma por defecto */
}

export interface StoreLanguage {
  code: string; /** Código del idioma */
  id: string; /** Identificador del idioma */
  name: string; /** Nombre del idioma */
  direction: 'rtl' | 'ltr'; /** Indicates if is a right-to-left language (ex. arabic/hebrew) or left-to-right (ex. spanish) */
}

export interface StoreCatalog {
  id: string; /** Identificador del catálogo */
  type?: string; /** Tipo del catálogo */
  label?: string; /** Etiqueta descriptiva del catálogo */
  identifier?: string;
}

export interface StoreDetailDetails {
  currencyInfo?: CurrencyInfo; /** Información sobre la moneda en base a país */
  alternateLocaleInfo?: AlternateLocaleInfo;
  location?: StoreDetailLocation;
  cmsConfigurations?: StoreCmsConfigurationsObject;
  timestampBuildVersion?: string;
  xConfigurations?: StoreXconfigurationsObject; /** Configuraciones (xconf) de back de la tienda */
  imageBaseUrl?: string; /** Ruta base para las imágenes (no xmedia) */
  staticBaseUrl?: string; /** Ruta base para los ficheros estáticos */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  _xmedia?: ApiStoreXmedia; /** Información del api cruda de utilidad para construir las urls de los medias con xmedia */
  translations?: ApiCmsTranslations; /** Traducciones de los componentes estáticas */
}

export interface StoreDetailLocation {
  latitude: number; /** Latitud central de el mercado */
  longitude: number; /** Longitud central de el mercado */
  zoom: number; /** Nivel de zoom a aplicar en el mapa renderizado */
}

export interface CurrencyInfo {
  currencyCode: string; /** Código de la moneda @example "EUR" */
  currencyFormatNeg: string; /** Máscara para precios negativos @example "-#,##0.00 € " */
  currencyFormatPos: string; /** Máscara para precios positivos @example "#,##0.00 € " */
  currencyDecimals: number; /** Número de decimales (exponente potencia de 10) @example -2 */
  currencySymbol: string; /** Símbolo de la moneda @example "€" */
  currencyDecimalChar: string; /** Carácter del decimal @example "," */
  currencyGroupingChar: string; /** Carácter del agrupador @example "." */
}

export interface AlternateLocaleInfo extends CurrencyInfo {
  conversionRate: number; /** Conversión a utilizar para calcular el valor de la moneda local en la moneda alternativa  */
}

export interface StoreCmsConfigurationsObject {
  enabledGDPR?: boolean; /** Indica si es una tienda afectada por la ley GDPR (políticas privacidad) */
  helpCenterQuestions?: HelpCenterTopic[]; /** Preguntas y respuestas centro de ayuda */
  filtersMap?: FiltersMapLabels[]; /** filters map @example "[{"value": "ColorFilter", "label": "Color"}]" */
  filterConfig?: GroupsFilters[]; /** categorias filtros */
  cookies?: Cookie[];
  lastSizesEnabled?: boolean; /** Indica si se deben destacar en parrilla los productos con últimas tallas */
  socialNetworks?: SocialNetwork[]; /** Redes sociales activas para compartir productos */
  newsletterPrivacyPolicies?: NewsletterPrivacyPolicy[]; /** Políticas de privacidad a aceptar al suscribirse a la newsletter */
  enableAnalyticsRender?: boolean; /** Indica si la analítica está activa en el render, es decir si se lanzan los eventos */
  gtmId?: string; /** Id del contenedor de Google Tag Manager utilizado */
}

export interface Cookie {
  value: string; /** Valor a guardar en la cookie. ex: test=true; */
  text: string; /** Texto informativo sobre la cookie */
  policy: string; /** Texto para el enlace de política de Cookies */
  policyLink: string; /** Enlace para la política de Cookies */
}

/** groups filters */
export interface GroupsFilters {
  title: string; /** filter category */
  name: string; /** filter category name */
  order?: number; /** filter order position */
}

/** Value labels filters */
export interface FiltersMapLabels {
  value: string; /** filter type */
  label: string; /** filter type label */
}

/** Store CMSConfigurartions Models */
export interface HelpCenterTopic {
  name: string; /** Nombre del topic @example Mi cuenta */
  slug: string; /** path al topic @example mi-cuenta */
  icon: string; /** path del icono @example mi-cuenta */
  questions: HelpCenterQuestion[]; /** Listado de preguntas y respuestas */
  activated?: boolean; /** Indica si el topic está activo o no */
}

export interface HelpCenterQuestion {
  text: string; /** Texto con la pregunta @example ¿Cómo me registro? */
  response: string; /** Texto con la respuesta @example Accediendo a la sección registros */
  slug: string; /** path a la respuesta @example como-registrarse */
}

export interface SocialNetwork {
  name: string; /** Nombre de la red social @example facebook */
  url?: string; /** Enlace al que se debe navegar al pulsar sobre ella */
  icon?: string; /** Nombre del icono que representa la red social @example email-line */
}

export interface NewsletterPrivacyPolicy {
  id: string; /** Id que identifica la política de privacidad */
  name: string; /** Nombre de la política de privacidad */
  label: string; /** Texto que se muestra en el check para marcar la política de privacidad */
  checkAll?: boolean; /** Si al marcar la opción se marcan todas las políticas de privacidad */
}

/** Stores Helpers Models */
export interface ContinentStores {
  id: string; /** Identificador del continente */
  name: string; /** Nombre del continente */
  nameES: string; /** Nombre en español del continente */
  stores: StoreSummary[]; /** Listado de tiendas */
}

export enum SalesStatusTypes {
  WORLD,
  SALE,
  NO_SALE,
  WORLD_WIDE
}

export interface GroupStore {
  id: SalesStatusTypes; /** Identificador del mercado */
  name: string; /** Nombre del mercado */
  continents: ContinentStores[]; /** Listado de continentes */
}


// TODO: improve name and remove from store.models?
export interface StoresMap {
  [name: string]: StoreSummary; /** Tiendas agrupadas por countryCode */
}

export type StoreXmedia = ApiStoreXmedia;

export interface ContinentStoresConfig {
  id: string;
  name: string;
  nameES: string;
  stores: number[];
}

export interface StoreXconfigurationsObject {
  liveSorting?: boolean; /** Si se recibe habilitado se ordenan los productos de la parrilla en base al sequence del producto */
  lastSizesEnabled?: boolean; /** Si se deben mostrar en una sección nueva de parrilla los productos con últimas talllas */
}

export interface PhysicalStore {
  id: string; /** Id de la tienda */
  name: string; /** Nombre de la tienda, normalmente compuesto por la calle */
  photo: string; /** Imagen de la tienda */
  addressLines: string[]; /** Dirección de la tienda */
  city: string; /** Ciudad de la tienda */
  zipCode: string; /** Código postal */
  state?: string; /** Provincia o estado */
  phones: string[]; /** Teléfonos disponibles */
  latitude: number; /** Latitud */
  longitude: number; /** Longitud */
  url: string; /** Url propia de la tienda */
  schedule?: PhysicalStoreSchedule[];
}

export interface PhysicalStoreSchedule {
  hours: string[];
  days: number[];
  daysText?: string;
}

export interface PhysicalStoresOptions {
  favouriteStores?: boolean; /** Si es true, recibe las tiendas que el usuario ha guardado como favoritas */
  lastStores?: boolean; /** Si es true, recibe las últimas tiendas que el usuario ha utilizado para compras */
  closerStores?: boolean; /** Si es true, recibe las tiendas ordenadas desde el centro de las coords */
  latitude: number; /** Latitud donde se buscarán tiendas */
  longitude: number; /** Longitud donde se buscarán tiendas */
  minStores?: number; /** Número mínimo de tiendas a recibir */
  maxStores?: number; /** Número máximo de tiendas a recibir */
  receiveEcommerce?: boolean; /** Indica si la tienda tiene ecommerce */
  countryCode?: string; /** País donde se buscará */
  radioMax?: number; /** Radio máximo de busqueda dadas las coordenadas */
}
